import authHeader from "@/services/auth/AuthHeader";
import axios from "axios";

/**
 * The merchant's own homepage — the free front page they build themselves.
 *
 * The logged-in merchant (ADMIN) reads and writes only their own: CagistaServices `/api/site/**` is
 * tenant-isolated by the SecurityCheckInterceptor, which checks the `merchant` we send against the
 * authenticated user. One page per merchant, not per location — the front page sits above the
 * locations, and a chain says so with a "våre steder" block.
 *
 * <b>Draft and published are different documents.</b> `save` writes the draft and changes nothing a
 * guest sees; `publish` is the deliberate act, and the only one that takes a version snapshot.
 */
class SiteHomeService {
    /** {schema, source, publishedVersion, publishedTime, storefrontUrl} — schema is always usable. */
    getHome(){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.get('/api/site/getHome?merchant=' + user.merchant, { headers: authHeader() });
    }

    /** Autosave. Does not touch the live page. */
    save(schema){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/saveHome', { merchant: user.merchant, schema: schema }, { headers: authHeader() });
    }

    /** Makes it live. The schema is sent so publishing cannot race its own autosave. */
    publish(schema){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/publishHome', { merchant: user.merchant, schema: schema }, { headers: authHeader() });
    }

    /** The page layouts — one restaurant, or several — with the fitting one marked. */
    getLayouts(){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.get('/api/site/homeLayouts?merchant=' + user.merchant, { headers: authHeader() });
    }

    /** Rebuild the section list to a layout. Returns the new schema WITHOUT saving it. */
    applyLayout(key, schema){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/applyHomeLayout',
            { merchant: user.merchant, key: key, schema: schema }, { headers: authHeader() });
    }

    /** The starting points the editor offers — key, name, one line, and the look. */
    getTemplates(){
        return axios.get('/api/site/homeTemplates', { headers: authHeader() });
    }

    /**
     * Swap the look. Returns the new schema WITHOUT saving it, so trying the alternatives is undone by
     * the same Forkast endringer as any other edit — and a section that survives keeps its content.
     */
    applyTemplate(key, schema){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/applyHomeTemplate',
            { merchant: user.merchant, key: key, schema: schema }, { headers: authHeader() });
    }

    getVersions(){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.get('/api/site/getHomeVersions?merchant=' + user.merchant, { headers: authHeader() });
    }

    /** Loads an old version into the DRAFT. The live page is untouched until they publish again. */
    restore(version){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/restoreHome', { merchant: user.merchant, version: version }, { headers: authHeader() });
    }

    /** Throws the draft away and starts again from what is live. */
    discardDraft(){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/discardHomeDraft', { merchant: user.merchant }, { headers: authHeader() });
    }

    /** On, or off for only the ordering site: their address then goes straight to the menu. The page is kept. */
    setHomeEnabled(enabled){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/site/setHomeEnabled', { merchant: user.merchant, enabled: enabled === true }, { headers: authHeader() });
    }
}

export default new SiteHomeService();
